import React, { useEffect, useState } from "react"
import { Link, useLocation, useMatch, useNavigate } from "react-router-dom"
import styled from "styled-components"

import CursorDefaultClick from "@autouncle/icons/dist/assets/cursor-default-click.svg?react"
import { RowsContainer } from "component_library/components/react/FlexDiv"
import CategoryButton from "./CategoryButton"
import Divider from "./Divider"
import NavigationLink from "./NavigationLink"
import { RegularLink } from "./NavigationLinks"
import SidebarHeader from "./SidebarHeader"

const EmptyIcon = styled.div`
  width: var(--icon-width-lg);
  height: var(--icon-width-lg);
`

type SettingsProps = {
  sidebarCollapsed: boolean
}

type Category = "webModules"
type CategoryToggles = Record<Category, boolean>

const NavigationLinkComponent = RegularLink.withComponent(Link)

const SettingsNavigation = ({ sidebarCollapsed }: SettingsProps) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const isSettings = Boolean(useMatch("/pro/settings/webmodules/*"))
  const isWebModules = Boolean(useMatch("/pro/webmodules/*"))
  // Whether each category is active in the current path
  const categoriesActiveFromUrl: CategoryToggles = {
    webModules: isSettings || isWebModules,
  }

  /**
   * Keeps a state of categories toggled manually
   * The initial state is based on the matches from current url
   */
  const [categoriesToggle, setCategoriesToggle] = useState<CategoryToggles>({
    ...categoriesActiveFromUrl,
  })

  const handleCategoryToggle = (category: Category) => {
    setCategoriesToggle((prev) => ({ ...prev, [category]: !prev[category] }))
  }

  useEffect(
    () => {
      /**
       * Whenever the category changes because of url changes, if a category was previously toggled,
       * it will stay toggled. Otherwise, it will be toggled according to the new url.
       */
      setCategoriesToggle((prev) =>
        Object.entries(prev).reduce<CategoryToggles>(
          (acc, [category, toggled]: [Category, boolean]) => ({
            ...acc,
            [category]: toggled || categoriesActiveFromUrl[category],
          }),
          prev
        )
      )
    },
    /**
     * We are only interested when the values change, not the object itself,
     * because the object is generated every render and it will create an infinite loop
     */
    Object.values(categoriesActiveFromUrl)
  )

  return (
    <RowsContainer>
      <SidebarHeader onBack={() => navigate("/")}>Settings</SidebarHeader>
      <Divider margin="0px" />
      <CategoryButton
        label="Web Modules"
        selected={categoriesActiveFromUrl.webModules}
        expanded={categoriesToggle.webModules || false}
        sidebarCollapsed={sidebarCollapsed}
        icon={CursorDefaultClick}
        onClick={() => handleCategoryToggle("webModules")}
      />
      {categoriesToggle.webModules && (
        <>
          <NavigationLink
            Icon={EmptyIcon}
            label="Visual appearance"
            active={true}
            link={{
              to: "/pro/settings/webmodules/visual",
              component: NavigationLinkComponent,
            }}
            currentPathname={pathname}
            sidebarCollapsed={sidebarCollapsed}
          />
          <NavigationLink
            Icon={EmptyIcon}
            label="Lead flows"
            active={true}
            link={{
              to: "/pro/webmodules/leadflows",
              component: NavigationLinkComponent,
              additionalActiveRoutes: [
                (path: string) => "/pro/webmodules/leadflows/create" === path,
              ],
            }}
            currentPathname={pathname}
            sidebarCollapsed={sidebarCollapsed}
          />
        </>
      )}
    </RowsContainer>
  )
}

export default SettingsNavigation
